import type { GraphQLError } from 'graphql';
import { useCallback } from 'react';
import type { GraphQLTaggedNode } from 'react-relay';
import { graphql } from 'react-relay';
import type { ApolloError } from '@confluence/apollo-relay-interop/entry-points/apolloTypes.ts';

import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { useLazyQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useLazyQueryInterop.ts';
import { usePageSpaceKey } from '@confluence/page-context';
import { useIsLicensed } from '@confluence/session-data/entry-points/useAccessStatus.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';

import type { SpacesListSpaceFragment as SpaceFragment } from './__types__/SpacesListSpaceFragment.ts';
import type {
	useSpacesDataMyVisitedSpacesQuery$data,
	useSpacesDataMyVisitedSpacesQuery$variables,
} from './__generated__/useSpacesDataMyVisitedSpacesQuery.graphql.ts';
import type {
	useSpacesDataMyVisitedSpacesWithCurrentSpaceQuery$data,
	useSpacesDataMyVisitedSpacesWithCurrentSpaceQuery$variables,
} from './__generated__/useSpacesDataMyVisitedSpacesWithCurrentSpaceQuery.graphql.ts';
import type {
	useSpacesDataStarredSpacesQuery$data,
	useSpacesDataStarredSpacesQuery$variables,
} from './__generated__/useSpacesDataStarredSpacesQuery.graphql.ts';
import type {
	useSpacesDataAnonymousSpacesQuery$data,
	useSpacesDataAnonymousSpacesQuery$variables,
} from './__generated__/useSpacesDataAnonymousSpacesQuery.graphql.ts';
import type {
	useSpacesDataSearchSpacesQuery$data,
	useSpacesDataSearchSpacesQuery$variables,
} from './__generated__/useSpacesDataSearchSpacesQuery.graphql.ts';

export const useSpacesDataStarredSpacesQuery: GraphQLTaggedNode = graphql`
	query useSpacesDataStarredSpacesQuery {
		spaces(favourite: true, first: 30, status: "current") {
			__typename
			nodes {
				alias
				id
				name
				key
				status
				icon {
					path
				}
			}
		}
	}
`;

const useSpacesDataMyVisitedSpacesQuery = graphql`
	query useSpacesDataMyVisitedSpacesQuery {
		myVisitedSpaces(limit: 26) {
			nodes {
				spaces {
					alias
					id
					name
					key
					status
					icon {
						path
					}
				}
			}
		}
	}
`;

const useSpacesDataMyVisitedSpacesWithCurrentSpaceQuery = graphql`
	query useSpacesDataMyVisitedSpacesWithCurrentSpaceQuery($selectedSpaceKey: String) {
		space(key: $selectedSpaceKey) {
			alias
			id
			name
			key
			status
			icon {
				path
			}
			currentUser {
				isFavourited
			}
		}
		myVisitedSpaces(limit: 26) {
			nodes {
				spaces {
					alias
					id
					name
					key
					status
					icon {
						path
					}
				}
			}
		}
	}
`;

const useSpacesDataAnonymousSpacesQuery = graphql`
	query useSpacesDataAnonymousSpacesQuery {
		spaces(first: 10) {
			nodes {
				alias
				id
				name
				key
				status
				icon {
					path
				}
			}
		}
	}
`;

const useSpacesDataSearchSpacesQuery = graphql`
	query useSpacesDataSearchSpacesQuery($spaceNamePattern: String!, $isLicensed: Boolean!) {
		spaces(first: 30, status: "current", spaceNamePattern: $spaceNamePattern) {
			nodes {
				alias
				id
				name
				key
				status
				icon {
					path
				}
				currentUser @include(if: $isLicensed) {
					isFavourited
				}
			}
		}
	}
`;

export type Space = SpaceFragment & {
	currentUser?: {
		isFavourited: boolean;
	};
};

type SearchedSpace = SpaceFragment & {
	currentUser: {
		isFavourited: boolean;
	};
};

const hasCurrentSpace = (
	data:
		| useSpacesDataMyVisitedSpacesQuery$data
		| useSpacesDataMyVisitedSpacesWithCurrentSpaceQuery$data,
): data is useSpacesDataMyVisitedSpacesWithCurrentSpaceQuery$data => 'space' in data;

const isCurrentSpaceNotFoundError = (gqlError: GraphQLError) =>
	gqlError.path?.[0] === 'space' && gqlError.extensions?.['statusCode'] === 404;

const processStarredError = (error?: ApolloErrorInterop): ApolloErrorInterop | undefined => {
	if (!error) {
		return undefined;
	}

	if (isUnauthorizedError(error)) {
		markErrorAsHandled(error);
		return undefined;
	}

	return error;
};

const processRecentError = (error?: ApolloErrorInterop): ApolloErrorInterop | undefined => {
	if (!error) {
		return undefined;
	}

	/**
	 * space
	 *    403: Expected to occur when the user is on a restricted space
	 *    404: Expected to occur when the user is on an invalid space
	 * recentSpaces
	 *    403: Harmless error temporarily being ignored to mitigate CONFDEV-71458.
	 *         Removing in CONFDEV-71656.
	 */
	if (
		isUnauthorizedError(error) ||
		(!error.networkError && error.graphQLErrors.every(isCurrentSpaceNotFoundError))
	) {
		markErrorAsHandled(error);
		return undefined;
	}

	return error;
};

type UseStarredDataOptions = {
	isLicensed: boolean;
};

export const useStarredData = ({
	isLicensed,
}: UseStarredDataOptions): {
	starredSpaces: Space[];
	loading: boolean;
	error: ApolloErrorInterop | undefined;
} => {
	const {
		data: starredSpacesData,
		loading: starredSpacesLoading,
		error: starredSpacesError,
	} = useQueryInterop<
		useSpacesDataStarredSpacesQuery$data,
		useSpacesDataStarredSpacesQuery$variables
	>(useSpacesDataStarredSpacesQuery, {
		skip: !isLicensed,
	});

	const {
		data: anonymousSpacesData,
		loading: anonymousSpacesLoading,
		error: anonymousSpacesError,
	} = useQueryInterop<
		useSpacesDataAnonymousSpacesQuery$data,
		useSpacesDataAnonymousSpacesQuery$variables
	>(useSpacesDataAnonymousSpacesQuery, {
		skip: isLicensed,
	});

	const data = isLicensed ? starredSpacesData : anonymousSpacesData;
	const loading = isLicensed ? starredSpacesLoading : anonymousSpacesLoading;
	const error = isLicensed ? starredSpacesError : anonymousSpacesError;

	const starredSpaces: Space[] = (data?.spaces?.nodes || [])
		.filter((space): space is Space => space != null)
		.map((space) => ({
			...space,
			currentUser: {
				isFavourited: isLicensed,
			},
		}));
	const unexpectedError = processStarredError(error);

	return {
		starredSpaces,
		loading,
		error: unexpectedError,
	};
};

const useRecentData = (isLicensed: boolean) => {
	const [spaceKey] = usePageSpaceKey();

	const {
		data: myVisitedSpacesWithCurrentSpaceData,
		loading: myVisitedSpacesWithCurrentSpaceLoading,
		error: myVisitedSpacesWithCurrentSpaceError,
	} = useQueryInterop<
		useSpacesDataMyVisitedSpacesWithCurrentSpaceQuery$data,
		useSpacesDataMyVisitedSpacesWithCurrentSpaceQuery$variables
	>(useSpacesDataMyVisitedSpacesWithCurrentSpaceQuery, {
		skip: !isLicensed || !spaceKey,
		variables: { selectedSpaceKey: spaceKey },
		errorPolicy: 'all',
	});

	const {
		data: myVisitedSpacesData,
		loading: myVisitedSpacesLoading,
		error: myVisitedSpacesError,
	} = useQueryInterop<
		useSpacesDataMyVisitedSpacesQuery$data,
		useSpacesDataMyVisitedSpacesQuery$variables
	>(useSpacesDataMyVisitedSpacesQuery, {
		skip: !isLicensed || !!spaceKey,
		errorPolicy: 'all',
	});

	const data = spaceKey ? myVisitedSpacesWithCurrentSpaceData : myVisitedSpacesData;
	const loading = spaceKey ? myVisitedSpacesWithCurrentSpaceLoading : myVisitedSpacesLoading;
	const error = spaceKey ? myVisitedSpacesWithCurrentSpaceError : myVisitedSpacesError;

	const currentSpace: Space | undefined =
		((data && hasCurrentSpace(data) && data.space) as Space) || undefined;

	const recentData = (data as useSpacesDataMyVisitedSpacesQuery$data)?.myVisitedSpaces?.nodes
		?.spaces;

	const recentSpaces: Space[] = (recentData || []).filter(
		(space): space is Space => space != null && (!currentSpace || currentSpace.id !== space.id),
	);

	const unexpectedError = processRecentError(error);

	return {
		recentSpaces,
		currentSpace,
		loading,
		error: unexpectedError,
	};
};

export const useSpacesData = (): {
	errors: ApolloErrorInterop[];
	loading: boolean;
	starredSpaces: Space[];
	recentSpaces: Space[];
	currentSpace: Space;
} => {
	const { isLicensed } = useSessionData();

	const {
		starredSpaces,
		loading: starredLoading,
		error: starredError,
	} = useStarredData({ isLicensed });

	const {
		recentSpaces: unfilteredRecents,
		currentSpace,
		loading: recentLoading,
		error: recentError,
	} = useRecentData(isLicensed);

	const starredIds = new Set(starredSpaces.map((starred) => starred.id));
	const recentSpaces = unfilteredRecents.filter((recent) => !starredIds.has(recent.id)).slice(0, 5);

	const errors: ApolloErrorInterop[] = [];
	if (starredError) errors.push(starredError);
	if (recentError) errors.push(recentError);

	return {
		errors,
		loading: starredLoading || recentLoading,
		starredSpaces,
		recentSpaces,
		currentSpace,
	};
};

export const useSpaceSearchData = (): {
	spaceSearchResults: SearchedSpace[];
	loading: boolean;
	error: ApolloError | undefined;
	searchSpaces: (pattern: string) => void;
} => {
	const isLicensed = useIsLicensed();

	const [executeSearch, { data, loading, error }] = useLazyQueryInterop<
		useSpacesDataSearchSpacesQuery$data,
		useSpacesDataSearchSpacesQuery$variables
	>(useSpacesDataSearchSpacesQuery);

	const spaceSearchResults: SearchedSpace[] = (data?.spaces?.nodes || []).filter(
		(space): space is SearchedSpace => space != null,
	);

	const searchSpaces = useCallback(
		(pattern: string): void => {
			if (pattern) {
				executeSearch({
					variables: {
						spaceNamePattern: pattern,
						isLicensed,
					},
				});
			}
		},
		[executeSearch, isLicensed],
	);

	return {
		spaceSearchResults,
		loading,
		error,
		searchSpaces,
	};
};
